import React from 'react';
import {
  DocPage, PageHeader, Section, SubSection,
  Para, Callout, DocTable, Prerequisites, NextStep, Tooltip,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './Meter.css';

const TOC = [
  { id: 'overview',     label: 'Overview' },
  { id: 'examples',    label: 'Examples' },
  { id: 'mapping',     label: 'Mapping' },
  { id: 'relationship', label: 'Relationship to other concepts' },
];

// ── Example pair card ─────────────────────────────────────────────────────────

function ExampleCard({ feeComponent, attribute, unit, description }) {
  return (
    <div className="ua-example">
      <div className="ua-example-pair">
        <div className="ua-example-side ua-example-side--fc">
          <span className="ua-example-label">Fee</span>
          <span className="ua-example-value">{feeComponent}</span>
          <span className="ua-example-sublabel">customer-facing</span>
        </div>
        <div className="ua-example-arrow">↔</div>
        <div className="ua-example-side ua-example-side--ua">
          <span className="ua-example-label">Meter</span>
          <span className="ua-example-value">{attribute}</span>
          <span className="ua-example-sublabel">measured in {unit}</span>
        </div>
      </div>
      <p className="ua-example-desc">{description}</p>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Meter({ onNavigate }) {
  const { industry } = useIndustry();
  const ua = industry.usageAttribute;

  const oneToManyHeaders = ['Meter', 'Fees driven'];
  const oneToManyRows = ua.oneToMany.map((r) => [r.attribute, r.feeComponents.join(', ')]);

  const description = (
    <>
      A Meter is the quantity tracked per usage event to drive a fee. The billing engine aggregates
      meter values across the period and prices the total through a{' '}
      <Tooltip tip="The pricing rule for a fee — defines the model (fixed, per-unit, tiered) and the rates. The usage total is the input; the rate card produces the charge amount.">
        rate card
      </Tooltip>
      .
    </>
  );

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Products"
        title="Meter"
        description={description}
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[{ id: 'product', label: 'Products' }]}
      />

      <Section id="overview" title="Overview">
        <Para>
          Every charge on an invoice has two sides: what the customer sees, and how the system
          measures it internally. A fee is the customer-facing label —{' '}
          <em>{ua.examples[0].feeComponent}</em>, <em>{ua.examples[1].feeComponent}</em>.
          A meter is the internal metric —{' '}
          <em>{ua.examples[0].attribute} ({ua.examples[0].unit})</em>,{' '}
          <em>{ua.examples[1].attribute} ({ua.examples[1].unit})</em>.
        </Para>
        <Para>
          This separation is deliberate. It lets you change how you measure usage without changing
          how charges appear on the invoice, and it lets the same metric drive multiple different
          fees.
        </Para>
        <Callout type="info" title="Configured at the product level">
          Meters are defined at the <strong>Product</strong> level — not on individual
          fees. A product must have its <em>usage-based</em> setting enabled before usage
          attributes can be added. Once defined, a meter can drive any fee
          associated with the same product. Meters are not shared across products.
        </Callout>
        <Callout type="info" title="What is a usage event?">
          A <strong>usage event</strong> is a record of consumption you send to the billing system —
          for example, "customer A recorded {ua.examples[0].attribute.toLowerCase()} of 50 {ua.examples[0].unit} on March 15".
          Each event carries one or more meter values. The billing engine collects
          all events in a billing period and aggregates them to compute the charge.
        </Callout>
      </Section>

      <Section id="examples" title="Examples">
        {ua.examples.map((ex) => (
          <ExampleCard key={ex.attribute} {...ex} />
        ))}
        <Callout type="tip" title="Why the separation matters">
          If you decide to change your pricing metric, you add a new meter and remap your
          fee — the invoice label stays the same. Customers see no change; only the
          internal measurement logic shifts.
        </Callout>
      </Section>

      <Section id="mapping" title="Meter to Fee mapping">
        <Para>
          A meter does not map to exactly one fee — the relationship is flexible
          in both directions. A meter and the fees it drives must all be
          associated with the same product.
        </Para>

        <SubSection id="mapping-one-to-many" title="One attribute → multiple fees">
          <Para>
            The same measurement can drive several charges simultaneously. In{' '}
            {industry.label.toLowerCase()} billing, a single meter often increments
            multiple fees within the same billing cycle:
          </Para>
          <DocTable headers={oneToManyHeaders} rows={oneToManyRows} />
        </SubSection>

        <SubSection id="mapping-many-to-one" title="One fee → multiple attributes">
          <Para>
            A single fee can be driven by more than one meter, with separate
            rate rules for each. For example, a <em>{ua.manyToOne.feeComponent}</em> fee
            might accumulate from both{' '}
            {ua.manyToOne.attributes.map((a, i) => (
              <span key={a}>
                {i > 0 && ' and '}<em>{a}</em>
              </span>
            ))}.
          </Para>
        </SubSection>
      </Section>

      <Section id="relationship" title="Relationship to other concepts">
        <Para>
          A meter sits between raw usage events and the fees that appear on
          invoices:
        </Para>
        <div className="ua-flow">
          <div className="ua-flow-step">Usage event <span className="ua-flow-sub">raw data</span></div>
          <div className="ua-flow-arrow">↓</div>
          <div className="ua-flow-product">
            <div className="ua-flow-product-label">Product</div>
            <div className="ua-flow-step ua-flow-step--highlight">Meter <span className="ua-flow-sub">measurement + unit</span></div>
            <div className="ua-flow-arrow ua-flow-arrow--label">↓ multiplier applied here</div>
            <div className="ua-flow-step">Fee <span className="ua-flow-sub">invoice line item</span></div>
          </div>
          <div className="ua-flow-arrow">↓</div>
          <div className="ua-flow-step">Invoice</div>
        </div>
        <Para>
          Meters are configured at the product level. Fee components associated with the
          same product declare which meter(s) drive their billing quantity. When a usage
          event is ingested, the system resolves the fee's linked meter, applies
          the multiplier, and accumulates the quantity toward the billing period.
        </Para>
      </Section>

      <NextStep
        onNavigate={onNavigate}
        items={[
          { id: 'fee', title: 'Fees', desc: 'Each fee selects which meter measures its consumption.' },
          { id: 'rate-card',     title: 'Rate Cards',     desc: 'Set the pricing rule that converts the measured quantity into a charge.' },
        ]}
      />
    </DocPage>
  );
}
